// src/pages/Personnaliser.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import InformationsSociete from "../components/InformationsSociete";
import FooterSociete from "../components/FooterSociete";
import MentionsLegales from "../components/MentionsLegales";
import axiosInstance from "../axiosInstance";
import UnsavedDialog from "../components/UnsavedDialog";
import TableStyleEditor from "../components/TableStyleEditor";
/* -------- helpers URL -------- */
const getTabFromSearch = (search) => {
  const params = new URLSearchParams(search || "");
  const t = params.get("tab");
  return ["societe", "footer", "mentions", "tableau"].includes(t) ? t : "societe";
};
const pathWithTab = (tab) => {
  const url = new URL(window.location.href);
  url.searchParams.set("tab", tab);
  return url.pathname + url.search + url.hash;
};

export default function Personnaliser() {
  const navigate = useNavigate();
  const location = useLocation();

  /* -------- état principal -------- */
  const [ongletActif, setOngletActif] = useState(getTabFromSearch(location.search));
  const [infosSociete, setInfosSociete] = useState({
    nom: "", forme: "", adresse: "", ville: "", pays: "",
    telephone: "", email: "", site: "", logo: "",
    tva: "", ape: "", capital: "", iban: "", immat: "", assureur: "",
  });

  /* -------- état "unsaved" global -------- */
  const [isDirty, setIsDirty] = useState(false);
  const saveHandlerRef = useRef(null);          // fourni par l’enfant courant
  const pendingNavRef = useRef(null);           // action de nav à exécuter après choix
  const [confirmOpen, setConfirmOpen] = useState(false);

  // setter stable pour que les enfants nous donnent leur save handler
  const setSaveHandler = useCallback((fn) => {
    saveHandlerRef.current = typeof fn === "function" ? fn : null;
  }, []);

  /* -------- sync onglet <-> URL -------- */
  useEffect(() => {
    setOngletActif(getTabFromSearch(location.search));
  }, [location.search]);

  /* -------- navigation protégée -------- */
  const guardedNavigate = useCallback((action) => {
    if (isDirty) {
      pendingNavRef.current = action;
      setConfirmOpen(true);
    } else {
      action();
    }
  }, [isDirty]);

  const goTab = useCallback((tab) => {
    if (tab === ongletActif) return;
    const next = pathWithTab(tab);
    guardedNavigate(() => navigate(next));
  }, [navigate, ongletActif, guardedNavigate]);

  const goEmailTemplates = useCallback(() => {
    guardedNavigate(() => navigate("/email-templates"));
  }, [navigate, guardedNavigate]);

  /* -------- chargement infos société -------- */
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = ""; // nécessaire pour le prompt natif
      return "";
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  /* -------- beforeunload : fermer onglet / refresh -------- */
  useEffect(() => {
    function onBeforeUnload(e) {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = ""; // requis pour afficher le prompt natif
      return "";
    }

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload); // ✅ même nom, même casse
    };
  }, [isDirty]);
  // NOTE: attention à bien garder la casse identique "onBeforeUnload" partout

  /* -------- callbacks pop-up -------- */
  const handleDialogSaveAndExit = useCallback(async () => {
    try {
      if (saveHandlerRef.current) {
        await Promise.resolve(saveHandlerRef.current());
      }
      setIsDirty(false);
      setConfirmOpen(false);
      const next = pendingNavRef.current;
      pendingNavRef.current = null;
      if (typeof next === "function") next();
    } catch (e) {
      console.error("Erreur de sauvegarde avant navigation :", e);
      // on ferme la modale, on reste sur place
      setConfirmOpen(false);
    }
  }, []);

  const handleDialogExit = useCallback(() => {
    // Quitter sans enregistrer
    setConfirmOpen(false);
    const next = pendingNavRef.current;
    pendingNavRef.current = null;
    if (typeof next === "function") next();
  }, []);

  const handleDialogCancel = useCallback(() => {
    // Annuler : rester sur la page
    setConfirmOpen(false);
    pendingNavRef.current = null;
  }, []);

  /* -------- rendu -------- */
  return (
    <div className="p-4 space-y-5">
      <h1 className="text-2xl font-bold text-slate-800">Personnaliser</h1>

      {/* Barre d’onglets */}
      <div className="bg-white rounded-xl border px-4 py-2">
        <div className="flex items-center gap-3">
          <div className="w-1" />
          <div className="flex-1 flex items-center justify-center">
            <div className="inline-flex items-center rounded-full border border-slate-300 overflow-hidden">
              <button
                type="button"
                className={`px-4 h-9 text-sm ${ongletActif === "societe" ? "bg-gray-300 text-black" : "bg-white text-slate-700"}`}
                onClick={() => goTab("societe")}
                aria-pressed={ongletActif === "societe"}
              >
                Informations société
              </button>
              <button
                type="button"
                className={`px-4 h-9 text-sm border-l border-slate-300 ${ongletActif === "footer" ? "bg-gray-300 text-black" : "bg-white text-slate-700"}`}
                onClick={() => goTab("footer")}
                aria-pressed={ongletActif === "footer"}
              >
                Pied de page
              </button>
              <button
                type="button"
                className={`px-4 h-9 text-sm border-l border-slate-300 ${ongletActif === "mentions" ? "bg-gray-300 text-black" : "bg-white text-slate-700"}`}
                onClick={() => goTab("mentions")}
                aria-pressed={ongletActif === "mentions"}
              >
                Mentions légales
              </button>
              <button
                type="button"
                className={`px-4 h-9 text-sm border-l border-slate-300 ${ongletActif === "tableau" ? "bg-gray-300 text-black" : "bg-white text-slate-700"}`}
                onClick={() => goTab("tableau")}
                aria-pressed={ongletActif === "tableau"}
              >
                Style du tableau
              </button>
              {/* Onglet externe */}
              <button
                type="button"
                className="px-4 h-9 text-sm border-l border-slate-300 bg-white text-slate-700 hover:bg-gray-50"
                onClick={goEmailTemplates}
                aria-label="Aller aux modèles d’e-mails"
              >
                Modèles d’e-mails
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="bg-white rounded-xl border p-4">
        {ongletActif === "societe" && (
          <>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Informations société</h2>
            <InformationsSociete
              infosSociete={infosSociete}
              setInfosSociete={setInfosSociete}
              onLogoChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const r = new FileReader();
                r.onloadend = () => setInfosSociete((p) => ({ ...p, logo: r.result }));
                r.readAsDataURL(file);
              }}
              onDirtyChange={setIsDirty}
              setSaveHandler={setSaveHandler}
            />
          </>
        )}

        {ongletActif === "footer" && (
          <>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Pied de page</h2>
            <FooterSociete
              infosSociete={infosSociete}
              setInfosSociete={setInfosSociete}
              onDirtyChange={setIsDirty}
              setSaveHandler={setSaveHandler}
            />
          </>
        )}

        {ongletActif === "mentions" && (
          <>
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Mentions légales</h2>
            <p className="mb-4 text-sm text-slate-600">
              Ajoutez vos mentions obligatoires qui apparaîtront sur vos devis et factures.
            </p>
            <MentionsLegales
              onDirtyChange={setIsDirty}
              setSaveHandler={setSaveHandler}
            />
          </>
        )}

        {ongletActif === "tableau" && (
          <>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Style du tableau (devis & factures)</h2>
            <TableStyleEditor
              onDirtyChange={setIsDirty}
              setSaveHandler={setSaveHandler}
            />
          </>
        )}

      </div>

      {/* Modale contrôlée */}
      <UnsavedDialog
        open={confirmOpen}
        onSaveAndExit={handleDialogSaveAndExit}
        onExit={handleDialogExit}
        onCancel={handleDialogCancel}
      />
    </div>
  );
}
